import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    Button,
    Alert,
    Table,
    ActionIcon,
    Loader,
    Tooltip,
    TextInput,
    Modal,
    Select,
    SegmentedControl
} from '@mantine/core';
import {
    IconAlertCircle,
    IconDownload,
    IconMail,
    IconPlus,
    IconSearch,
    IconX,
    IconCoins,
    IconUser,
    IconFilterOff,
    IconExternalLink
} from '@tabler/icons-react';
import { useGetAfipVouchers, useReservationsTotalSold } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';
import { formatDayLong, formatPrice, setWebTitle } from '@/lib/format-utils';
import { AFIP_GENERATE_PATH, RESERVATIONS_PATH } from '@/lib/navigation';
import { AfipVoucher } from '@turizi/shared';
import DatePickerCustom from '@/components/custom-date-picker';
import SendAfipVoucherEmailModal from '@/components/send-afip-voucher-email-modal';


export default function AfipHistoryPage(): JSX.Element {
    const { t } = useTranslation();
    const [direction, setDirection] = useState<'emitted' | 'received'>('emitted');
    const { data: vouchers, isLoading: vouchersLoading } = useGetAfipVouchers({ direction });

    setWebTitle(t('afip_vouchers'));

    // Filter states
    const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
    const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
    const [selectedClient, setSelectedClient] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const { data: soldData, isLoading: soldLoading } = useReservationsTotalSold({
        start_date: dateRange[0] || undefined,
        end_date: dateRange[1] || undefined,
    });

    // Download loading states by voucher id
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});

    // Send email modal state
    const [selectedVoucherForEmail, setSelectedVoucherForEmail] = useState<AfipVoucher | null>(null);

    // Unique currency options
    const currencyOptions = useMemo(() => {
        const set = new Set<string>();
        (vouchers || []).forEach((voucher: any) => {
            if (voucher.currency) set.add(voucher.currency);
        });
        return Array.from(set).map((curr) => ({
            value: curr,
            label: `${curr} - ${t(`currencies.${curr}`)}`
        }));
    }, [vouchers, t]);

    // Unique client/issuer options derived from vouchers
    const clientOptions = useMemo(() => {
        if (!vouchers || vouchers.length === 0) return [];
        const clientMap = new Map<string, { name: string; label: string }>();

        vouchers.forEach((voucher: AfipVoucher) => {
            const name = direction === 'received'
                ? (voucher.issuer_business?.name || voucher.issuer_name || '').trim()
                : (voucher.receiver_name || '').trim();
            if (!name) return;
            if (!clientMap.has(name)) {
                const docVal = direction === 'received' ? voucher.issuer_cuit : voucher.doc_nro;
                const docInfo = docVal && String(docVal) !== '0' ? ` (${docVal})` : '';
                clientMap.set(name, {
                    name,
                    label: `${name}${docInfo}`
                });
            }
        });

        return Array.from(clientMap.values())
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((c) => ({
                value: c.name,
                label: c.label
            }));
    }, [vouchers, direction]);

    // Filter logic
    const filteredVouchers = useMemo(() => {
        if (!vouchers) return [];

        return vouchers.filter((voucher: AfipVoucher) => {
            const effectiveDate = voucher.issue_date || voucher.created_at;
            // 1. Date filter
            if (dateRange[0]) {
                const voucherDay = dayjs(effectiveDate).format('YYYY-MM-DD');
                if (voucherDay < dateRange[0]) return false;
            }
            if (dateRange[1]) {
                const voucherDay = dayjs(effectiveDate).format('YYYY-MM-DD');
                if (voucherDay > dateRange[1]) return false;
            }

            // 2. Currency filter
            const voucherCurrency = (voucher as any).currency || 'ARS';
            if (selectedCurrency && voucherCurrency !== selectedCurrency) {
                return false;
            }

            // 3. Client/Issuer filter
            const partyName = direction === 'received'
                ? (voucher.issuer_business?.name || voucher.issuer_name || '').trim()
                : (voucher.receiver_name || '').trim();
            if (selectedClient && partyName !== selectedClient.trim()) {
                return false;
            }

            // 4. Text Search query
            const query = searchQuery.trim().toLowerCase();
            if (query) {
                const receiverNameMatches = voucher.receiver_name?.toLowerCase().includes(query);
                const issuerNameMatches = (voucher.issuer_business?.name || voucher.issuer_name || '').toLowerCase().includes(query);
                const docNroMatches = String(voucher.doc_nro || voucher.issuer_cuit || '').toLowerCase().includes(query);
                const voucherNumberMatches = String(voucher.voucher_number || '').includes(query);
                const caeMatches = voucher.cae?.toLowerCase().includes(query);

                const cleanQuery = query.replace(/^#/, '');
                const reservationMatches = voucher.reservations?.some((r: any) =>
                    String(r.id).includes(cleanQuery) || (r.slug && r.slug.toLowerCase().includes(query))
                );
                const bookingMatches = voucher.service_bookings?.some((sb: any) =>
                    String(sb.id).includes(cleanQuery) || String(sb.reservation_id || '').includes(cleanQuery)
                );

                if (!receiverNameMatches && !issuerNameMatches && !docNroMatches && !voucherNumberMatches && !caeMatches && !reservationMatches && !bookingMatches) {
                    return false;
                }
            }

            return true;
        });
    }, [vouchers, dateRange, selectedCurrency, selectedClient, searchQuery, direction]);

    // Summary calculation (reactive to filters)
    const summary = useMemo(() => {
        const totalsByCurrency: Record<string, {
            currency: string;
            totalAmount: number;
            totalNet: number;
            totalIva: number;
            count: number;
            byType: Record<number, number>;
        }> = {};

        filteredVouchers.forEach((voucher: AfipVoucher) => {
            const curr = (voucher as any).currency || 'ARS';
            if (!totalsByCurrency[curr]) {
                totalsByCurrency[curr] = {
                    currency: curr,
                    totalAmount: 0,
                    totalNet: 0,
                    totalIva: 0,
                    count: 0,
                    byType: {}
                };
            }
            const total = Number(voucher.imp_total || 0);
            const net = Number(voucher.imp_neto !== undefined && voucher.imp_neto !== null ? voucher.imp_neto : voucher.imp_total || 0);
            const iva = Number(voucher.imp_iva || 0);

            totalsByCurrency[curr].totalAmount += total;
            totalsByCurrency[curr].totalNet += net;
            totalsByCurrency[curr].totalIva += iva;
            totalsByCurrency[curr].count += 1;
            totalsByCurrency[curr].byType[voucher.cbte_tipo] = (totalsByCurrency[curr].byType[voucher.cbte_tipo] || 0) + 1;
        });

        const currencies = Object.keys(totalsByCurrency);
        if (currencies.length === 0) {
            totalsByCurrency['ARS'] = {
                currency: 'ARS',
                totalAmount: 0,
                totalNet: 0,
                totalIva: 0,
                count: 0,
                byType: {}
            };
            currencies.push('ARS');
        }

        return {
            totalsByCurrency,
            currencies,
            totalCount: filteredVouchers.length
        };
    }, [filteredVouchers]);

    // Active currencies for the summary table (including sales and vouchers)
    const summaryCurrencies = useMemo(() => {
        const set = new Set<string>();
        summary.currencies.forEach((c) => set.add(c));
        if (soldData?.totals) {
            Object.keys(soldData.totals).forEach((c) => set.add(c));
        }
        if (set.size === 0) set.add('ARS');

        let list = Array.from(set);
        if (selectedCurrency) {
            list = list.filter((c) => c === selectedCurrency);
            if (list.length === 0) list = [selectedCurrency];
        }
        return list.sort();
    }, [summary.currencies, soldData?.totals, selectedCurrency]);

    const hasActiveFilters = Boolean(
        dateRange[0] ||
        dateRange[1] ||
        selectedCurrency ||
        selectedClient ||
        searchQuery.trim()
    );

    const handleResetFilters = () => {
        setDateRange(['', '']);
        setSelectedCurrency(null);
        setSelectedClient(null);
        setSearchQuery('');
    };

    const handleDownload = async (voucher: AfipVoucher) => {
        setDownloadingIds(prev => ({ ...prev, [voucher.id]: true }));
        try {
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/afip/vouchers/${voucher.id}/download`, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to download PDF');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `comprobante-${voucher.pos}-${voucher.voucher_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            notify.error('error', 'failed_to_download_pdf');
        } finally {
            setDownloadingIds(prev => ({ ...prev, [voucher.id]: false }));
        }
    };


    const getVoucherTypeName = (type: number) => {
        switch (type) {
            case 1: return 'Factura A';
            case 2: return 'Nota de Débito A';
            case 3: return 'Nota de Crédito A';
            case 6: return 'Factura B';
            case 7: return 'Nota de Débito B';
            case 8: return 'Nota de Crédito B';
            case 11: return 'Factura C';
            case 12: return 'Nota de Débito C';
            case 13: return 'Nota de Crédito C';
            case 195: return 'Factura T';
            case 196: return 'Nota de Débito T';
            case 197: return 'Nota de Crédito T';
            default: return `Comprobante (${type})`;
        }
    };

    return (
        <div className="panel-content">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
                <SegmentedControl
                    size="xs"
                    value={direction}
                    onChange={(val) => {
                        setDirection(val as 'emitted' | 'received');
                        setSelectedClient(null);
                    }}
                    data={[
                        { label: t('emitted_vouchers'), value: 'emitted' },
                        { label: t('received_vouchers'), value: 'received' },
                    ]}
                />
                {direction === 'emitted' && (
                    <Button
                        size="xs"
                        color="black"
                        leftSection={<IconPlus size={16} />}
                        component={Link}
                        to={AFIP_GENERATE_PATH}
                    >
                        {t('generate_invoice')}
                    </Button>
                )}
            </div>

            {/* Filters Bar */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-2xs mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
                    {/* Date Filter */}
                    <div className="sm:col-span-2 lg:col-span-3">
                        <DatePickerCustom
                            size="sm"
                            valueFormat="DD MMM YYYY"
                            label={t('filter_by_date')}
                            clearable={true}
                            value={dateRange}
                            onChange={setDateRange}
                        />
                    </div>

                    {/* Currency Filter */}
                    <div className="sm:col-span-1 lg:col-span-2">
                        <Select
                            size="sm"
                            label={t('filter_by_currency')}
                            placeholder={t('all_currencies')}
                            data={currencyOptions}
                            value={selectedCurrency}
                            onChange={(value) => setSelectedCurrency(value)}
                            clearable
                            searchable
                            leftSection={<IconCoins size={16} stroke={1.5} />}
                        />
                    </div>

                    {/* Client Filter */}
                    <div className="sm:col-span-1 lg:col-span-3">
                        <Select
                            size="sm"
                            label={t('filter_by_client')}
                            placeholder={t('all_clients')}
                            data={clientOptions}
                            value={selectedClient}
                            onChange={(value) => setSelectedClient(value)}
                            clearable
                            searchable
                            leftSection={<IconUser size={16} stroke={1.5} />}
                        />
                    </div>

                    {/* Search Query */}
                    <div className="sm:col-span-2 lg:col-span-4">
                        <TextInput
                            size="sm"
                            label={t('search')}
                            placeholder={t('search_afip_vouchers')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.currentTarget.value)}
                            leftSection={<IconSearch size={16} />}
                            rightSection={searchQuery ? (
                                <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setSearchQuery('')}>
                                    <IconX size={14} />
                                </ActionIcon>
                            ) : null}
                        />
                    </div>
                </div>

                {hasActiveFilters && (
                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">
                            {t('total_vouchers')}: <span className="font-semibold text-gray-800">{filteredVouchers.length}</span>
                        </span>
                        <Button
                            variant="subtle"
                            color="gray"
                            size="xs"
                            onClick={handleResetFilters}
                            leftSection={<IconFilterOff size={14} />}
                        >
                            {t('clear_filters')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Compact Summary Table below filters */}
            <div className="mb-6">
                {soldLoading && <Loader size="xs" color="black" />}
                <div className="subtle-table">
                    <table>
                        <thead>
                            <tr>
                                <th className=''>{t('currency')}</th>
                                <th className='number'>{t('total_sold')}</th>
                                <th className='number'>{t('total_invoiced')}</th>
                                <th className='number'>{t('difference')}</th>
                                <th className='number'>{t('tax_net_amount')}</th>
                                <th className='number'>{t('iva_amount')}</th>
                                <th className='number'>{t('total_vouchers')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summaryCurrencies.map((curr) => {
                                const voucherData = summary.totalsByCurrency[curr] || {
                                    totalAmount: 0,
                                    totalNet: 0,
                                    totalIva: 0,
                                    count: 0,
                                    byType: {}
                                };
                                const soldAmount = Number(soldData?.totals?.[curr] || 0);
                                const invoicedAmount = Number(voucherData.totalAmount || 0);
                                const diff = soldAmount - invoicedAmount;

                                return (
                                    <tr key={curr}>
                                        <td className='font-bold'>{curr}</td>
                                        <td className='number'>
                                            {formatPrice(soldAmount)}
                                        </td>
                                        <td className='number'>
                                            {formatPrice(invoicedAmount)}
                                        </td>
                                        <td className='number'>
                                            {formatPrice(Math.abs(diff))}
                                        </td>
                                        <td className='number'>
                                            {formatPrice(voucherData.totalNet)}
                                        </td>
                                        <td className='number'>
                                            {formatPrice(voucherData.totalIva)}
                                        </td>
                                        <td className='number'>
                                            {voucherData.count}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {vouchersLoading ? (
                <div className="flex justify-center py-12">
                    <Loader color="black" />
                </div>
            ) : !vouchers || vouchers.length === 0 ? (
                <Alert icon={<IconAlertCircle size={18} />} color="gray" className="py-8">
                    <div className="text-center">{t('no_vouchers')}</div>
                </Alert>
            ) : filteredVouchers.length === 0 ? (
                <Alert icon={<IconAlertCircle size={18} />} color="gray" className="py-8">
                    <div className="text-center space-y-3">
                        <div>{t('no_results')}</div>
                        {hasActiveFilters && (
                            <Button
                                variant="light"
                                color="gray"
                                size="xs"
                                onClick={handleResetFilters}
                                leftSection={<IconFilterOff size={14} />}
                            >
                                {t('clear_filters')}
                            </Button>
                        )}
                    </div>
                </Alert>
            ) : (
                <div className="border border-gray-100 rounded-lg overflow-hidden bg-white">
                    <Table verticalSpacing="md" horizontalSpacing="lg">
                        <Table.Thead>
                            <Table.Tr className="bg-gray-50 border-b border-gray-200">
                                <Table.Th className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('date')}</Table.Th>
                                <Table.Th className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('invoice_number_result')}</Table.Th>
                                <Table.Th className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('invoice_type')}</Table.Th>
                                <Table.Th className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    {direction === 'received' ? t('issuer_name') : t('receiver')}
                                </Table.Th>
                                <Table.Th className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    {direction === 'received' ? t('services') : t('reservations')}
                                </Table.Th>
                                <Table.Th className="text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">{t('total_amount')}</Table.Th>
                                <Table.Th className="text-xs font-semibold text-gray-600 uppercase tracking-wider">CAE</Table.Th>
                                <Table.Th className="text-xs font-semibold text-gray-600 uppercase tracking-wider text-center">{t('actions')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {filteredVouchers.map((voucher: any) => (
                                <Table.Tr key={voucher.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors duration-150">
                                    <Table.Td className="text-sm font-medium text-gray-900 whitespace-nowrap">
                                        {formatDayLong(voucher.issue_date || voucher.created_at)}
                                    </Table.Td>
                                    <Table.Td className="text-sm text-gray-500 ">
                                        {String(voucher.pos).padStart(4, '0')}-{String(voucher.voucher_number).padStart(8, '0')}
                                    </Table.Td>
                                    <Table.Td className="text-sm text-gray-600">
                                        {getVoucherTypeName(voucher.cbte_tipo)}
                                    </Table.Td>
                                    <Table.Td className="text-sm text-gray-900">
                                        {direction === 'received' ? (
                                            <>
                                                <div className="font-semibold">
                                                    {voucher.issuer_business?.name || voucher.issuer_name || '-'}
                                                </div>
                                                {voucher.issuer_cuit && (
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        {voucher.issuer_cuit}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <div className="font-semibold">{voucher.receiver_name}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    {voucher.doc_nro}
                                                </div>
                                            </>
                                        )}
                                    </Table.Td>
                                    <Table.Td className="text-sm">
                                        {direction === 'received' ? (
                                            voucher.service_bookings && voucher.service_bookings.length > 0 ? (
                                                <div className="flex flex-wrap gap-1 items-center max-w-xs">
                                                    {voucher.service_bookings.map((sb: any) => (
                                                        <Link
                                                            key={sb.id}
                                                            to={sb.reservation_id ? `${RESERVATIONS_PATH}/${sb.reservation_id}` : '#'}
                                                            className="px-2 py-0.5 rounded bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-800 text-xs font-medium border border-gray-200/60 shadow-3xs inline-flex items-center gap-1 transition-colors"
                                                        >
                                                            <span className="font-semibold">#{sb.id}</span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">-</span>
                                            )
                                        ) : voucher.reservations && voucher.reservations.length > 0 ? (
                                            <div className="flex flex-wrap gap-1 items-center max-w-xs">
                                                {voucher.reservations.map((res: any) => (
                                                    <Link
                                                        key={res.id}
                                                        to={`${RESERVATIONS_PATH}/${res.id}`}
                                                        className="px-2 py-0.5 rounded bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-800 text-xs font-medium border border-gray-200/60 shadow-3xs inline-flex items-center gap-1 transition-colors"
                                                    >
                                                        <span className="font-semibold">#{res.id}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400">-</span>
                                        )}
                                    </Table.Td>
                                    <Table.Td className="text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                                        ${formatPrice(voucher.imp_total)}{' '}
                                        <span className="text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-200 px-1 py-0.5 rounded">
                                            {voucher.currency || 'ARS'}
                                        </span>
                                    </Table.Td>
                                    <Table.Td className="text-sm text-gray-500">
                                        {voucher.cae ? (
                                            <>
                                                <div className=" text-xs">{voucher.cae}</div>
                                                {voucher.cae_expiration && (
                                                    <div className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">
                                                        Vence: {formatDayLong(voucher.cae_expiration)}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-xs text-gray-400">-</span>
                                        )}
                                    </Table.Td>
                                    <Table.Td className="text-center">
                                        {direction === 'received' ? (
                                            <div className="flex items-center justify-center gap-1">
                                                {(voucher.attachments || []).map((att: string, idx: number) => (
                                                    <Tooltip key={idx} label={t('download')}>
                                                        <ActionIcon
                                                            variant="light"
                                                            color="blue"
                                                            component="a"
                                                            href={att}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <IconExternalLink size={16} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center gap-1">
                                                <Tooltip label={t('download')}>
                                                    <ActionIcon
                                                        variant="light"
                                                        color="gray"
                                                        onClick={() => handleDownload(voucher)}
                                                        loading={downloadingIds[voucher.id]}
                                                    >
                                                        <IconDownload size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                                <Tooltip label={t('send_by_email')}>
                                                    <ActionIcon
                                                        variant="light"
                                                        color="blue"
                                                        onClick={() => {
                                                            setSelectedVoucherForEmail(voucher);
                                                        }}
                                                    >
                                                        <IconMail size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </div>
            )}

            {/* Modal to Send Voucher by Email */}
            <SendAfipVoucherEmailModal
                opened={!!selectedVoucherForEmail}
                onClose={() => setSelectedVoucherForEmail(null)}
                voucher={selectedVoucherForEmail}
            />
        </div>
    );
}
